import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const experienceLevelLabels = {
  beginner: 'Başlangıç',
  intermediate: 'Orta Seviye',
  advanced: 'İleri Seviye'
};

const equipmentLabels = {
  yes: 'Kendi ekipmanım var',
  no: 'Kiralama ihtiyacım var',
  planning: 'Almayı planlıyorum'
};

const locationLabels = {
  istanbul: 'İstanbul',
  izmir: 'İzmir',
  antalya: 'Antalya',
  bodrum: 'Bodrum',
  marmaris: 'Marmaris',
  fethiye: 'Fethiye',
  cesme: 'Çeşme',
  alacati: 'Alaçatı',
  other: 'Diğer'
};

const onboardingSteps = [
  {
    icon: 'Zap',
    title: 'Deneyim & Ekipman',
    description: 'SUP deneyim seviyeni ve ekipman durumunu belirle',
    path: '/onboarding-flow',
    color: 'var(--color-primary)'
  },
  {
    icon: 'MapPin',
    title: 'Bölge & İlgi Alanları',
    description: 'Favori bölgelerini ve ilgi alanlarını seç',
    path: '/onboarding-flow',
    color: 'var(--color-secondary)'
  },
  {
    icon: 'Bell',
    title: 'Bildirim Tercihleri',
    description: 'Hangi bildirimleri almak istediğini ayarla',
    path: '/onboarding-flow',
    color: 'var(--color-accent)'
  }
];

const RegistrationSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  const registrationData = location?.state?.registrationData || {};
  const email = location?.state?.email || registrationData?.email || '';
  const fullName = location?.state?.fullName || registrationData?.fullName || '';
  const experienceLevel = location?.state?.experienceLevel || registrationData?.experienceLevel || '';
  const hasEquipment = location?.state?.hasEquipment || registrationData?.hasEquipment || '';
  const userLocation = location?.state?.location || registrationData?.location || '';

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const handleStartOnboarding = () => {
    navigate('/onboarding-flow');
  };

  const handleGoToLogin = () => {
    navigate('/login', {
      state: {
        message: 'Hesabınız oluşturuldu! Giriş yapabilirsiniz.',
        email
      }
    });
  };

  const handleGoToEvents = () => {
    navigate('/event-discovery');
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: 'var(--color-background)' }}
    >
      <div className="flex-1 flex items-center justify-center px-4 py-10 md:px-6 md:py-14">
        <div
          className="w-full max-w-2xl"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(18px)',
            transition: 'opacity 0.5s cubic-bezier(0.22,1,0.36,1), transform 0.5s cubic-bezier(0.22,1,0.36,1)'
          }}
        >
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-5">
              <div
                className="relative w-20 h-20 rounded-full flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, var(--color-success) 0%, #34d399 100%)',
                  boxShadow: '0 8px 32px rgba(5,150,105,0.28)'
                }}
              >
                <Icon name="CheckCircle" size={40} color="#fff" />
                <span
                  className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'var(--color-accent)', boxShadow: '0 2px 8px rgba(244,162,97,0.4)' }}
                >
                  <Icon name="Waves" size={13} color="#fff" />
                </span>
              </div>
            </div>
            <h1
              className="text-2xl md:text-3xl font-bold mb-2"
              style={{ color: 'var(--color-foreground)' }}
            >
              Hoş Geldin{fullName ? `, ${fullName?.split(' ')?.[0]}!` : '!'}
            </h1>
            <p className="text-base" style={{ color: 'var(--color-muted-foreground)' }}>
              PaddleTurkiye topluluğuna başarıyla katıldın.
            </p>
          </div>

          {/* Email Verification Status */}
          <div
            className="rounded-xl p-4 mb-5 flex items-start gap-3"
            style={{
              backgroundColor: 'rgba(245,158,11,0.08)',
              border: '1px solid rgba(245,158,11,0.25)'
            }}
          >
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: 'rgba(245,158,11,0.15)' }}
            >
              <Icon name="Mail" size={18} color="var(--color-warning)" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold" style={{ color: 'var(--color-foreground)' }}>
                  E-posta Doğrulaması Bekleniyor
                </span>
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: 'var(--color-warning)' }}
                >
                  Beklemede
                </span>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--color-muted-foreground)' }}>
                {email
                  ? <><strong style={{ color: 'var(--color-foreground)' }}>{email}</strong> adresine doğrulama e-postası gönderildi.</>
                  : 'Kayıt e-posta adresine doğrulama bağlantısı gönderildi.'
                }
                {' '}Gelen kutunu kontrol et ve hesabını etkinleştir.
              </p>
            </div>
          </div>

          {/* Profile Setup Recap */}
          {(fullName || experienceLevel || hasEquipment || userLocation) && (
            <div
              className="rounded-xl p-5 mb-5"
              style={{
                backgroundColor: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                boxShadow: '0 2px 12px rgba(27,75,102,0.06)'
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Icon name="User" size={16} color="var(--color-primary)" />
                <span className="text-sm font-semibold" style={{ color: 'var(--color-foreground)' }}>
                  Profil Özeti
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {fullName && (
                  <div
                    className="flex items-center gap-3 p-3 rounded-lg"
                    style={{ backgroundColor: 'var(--color-muted)' }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(27,75,102,0.1)' }}
                    >
                      <Icon name="UserCircle" size={15} color="var(--color-primary)" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Ad Soyad</p>
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--color-foreground)' }}>{fullName}</p>
                    </div>
                  </div>
                )}
                {email && (
                  <div
                    className="flex items-center gap-3 p-3 rounded-lg"
                    style={{ backgroundColor: 'var(--color-muted)' }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(46,125,138,0.1)' }}
                    >
                      <Icon name="AtSign" size={15} color="var(--color-secondary)" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>E-posta</p>
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--color-foreground)' }}>{email}</p>
                    </div>
                  </div>
                )}
                {experienceLevel && (
                  <div
                    className="flex items-center gap-3 p-3 rounded-lg"
                    style={{ backgroundColor: 'var(--color-muted)' }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(244,162,97,0.12)' }}
                    >
                      <Icon name="TrendingUp" size={15} color="var(--color-accent)" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Deneyim Seviyesi</p>
                      <p className="text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>
                        {experienceLevelLabels?.[experienceLevel] || experienceLevel}
                      </p>
                    </div>
                  </div>
                )}
                {hasEquipment && (
                  <div
                    className="flex items-center gap-3 p-3 rounded-lg"
                    style={{ backgroundColor: 'var(--color-muted)' }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(27,75,102,0.1)' }}
                    >
                      <Icon name="Package" size={15} color="var(--color-primary)" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Ekipman</p>
                      <p className="text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>
                        {equipmentLabels?.[hasEquipment] || hasEquipment}
                      </p>
                    </div>
                  </div>
                )}
                {userLocation && (
                  <div
                    className="flex items-center gap-3 p-3 rounded-lg sm:col-span-2"
                    style={{ backgroundColor: 'var(--color-muted)' }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(46,125,138,0.1)' }}
                    >
                      <Icon name="MapPin" size={15} color="var(--color-secondary)" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Konum</p>
                      <p className="text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>
                        {locationLabels?.[userLocation] || userLocation}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Onboarding Navigation */}
          <div
            className="rounded-xl p-5 mb-6"
            style={{
              backgroundColor: 'var(--color-card)',
              border: '1px solid var(--color-border)',
              boxShadow: '0 2px 12px rgba(27,75,102,0.06)'
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Compass" size={16} color="var(--color-primary)" />
              <span className="text-sm font-semibold" style={{ color: 'var(--color-foreground)' }}>
                Profili Tamamla
              </span>
            </div>
            <p className="text-xs mb-4" style={{ color: 'var(--color-muted-foreground)' }}>
Kayıt işlemlerini tamamlayarak kişiselleştirilmiş deneyim kazan
            </p>
            <div className="space-y-2.5 mb-4">
              {onboardingSteps?.map((step, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-3 rounded-lg"
                  style={{
                    backgroundColor: 'var(--color-muted)',
                    border: '1px solid transparent'
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${step?.color}18` }}
                  >
                    <Icon name={step?.icon} size={15} color={step?.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>{step?.title}</p>
                    <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{step?.description}</p>
                  </div>
                  <div
                    className="w-5 h-5 rounded-full border-2 flex-shrink-0"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                </div>
              ))}
            </div>
            <Button
              onClick={handleStartOnboarding}
              className="w-full"
              style={{
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-primary-foreground)',
                padding: '10px 16px',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
            >
              <Icon name="ArrowRight" size={17} color="#fff" />
Kayıt olma adımlarını tamamla
            </Button>
          </div>

          {/* Secondary Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleGoToLogin}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium transition-all"
              style={{
                backgroundColor: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                color: 'var(--color-foreground)',
                cursor: 'pointer'
              }}
            >
              <Icon name="LogIn" size={15} color="var(--color-primary)" />
              Giriş Yap
            </button>
            <button
              onClick={handleGoToEvents}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium transition-all"
              style={{
                backgroundColor: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                color: 'var(--color-foreground)',
                cursor: 'pointer'
              }}
            >
              <Icon name="Calendar" size={15} color="var(--color-secondary)" />
              Etkinlikleri Keşfet
            </button>
          </div>
        </div>
      </div>
      {/* Footer */}
      <footer className="py-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
        <div className="max-w-2xl mx-auto px-4 text-center">
          <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
            © {new Date()?.getFullYear()} PaddleTurkiye. Tüm hakları saklıdır.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RegistrationSuccess;
